(() => {
  const APP_SELECTOR = '#hearing-test-app';

  const EMAIL_ENDPOINT =
    window.HearingTestEmailEndpoint ||
    'https://ouvirmelhor.dev.escolhadigital.com/api/hearing-test-result-email';

  function initializeHearingTests(container = document) {
    const apps = [];

    if (container.matches && container.matches(APP_SELECTOR)) {
      apps.push(container);
    }

    apps.push(...container.querySelectorAll(APP_SELECTOR));

    apps.forEach(initHearingTestApp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeHearingTests();
    });
  } else {
    initializeHearingTests();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeHearingTests(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[id^="shopify-section-"]');

    if (section) {
      initializeHearingTests(section);
    }
  });

  function initHearingTestApp(app) {
    if (!app || app.dataset.initialized === 'true') return;
    app.dataset.initialized = 'true';

    const sectionId = app.getAttribute('data-section');

    const configElement = app
      .closest('.hearing-test-section')
      ?.querySelector('[data-hearing-test-config]');

    let config = {};

    if (configElement) {
      try {
        config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.error('Configuração do teste auditivo inválida:', error);
      }
    } else {
      config =
        (window.HearingTestConfig && window.HearingTestConfig[sectionId]) || {};
    }

    const stage = app.querySelector('#hearing-test-stage');

    if (!stage) return;

    const questions = Array.isArray(config.questions) ? config.questions.filter(Boolean) : [];
    const frequencies = Array.isArray(config.frequencies)
      ? config.frequencies.map(Number).filter(Boolean)
      : [250, 500, 1000, 2000, 4000, 8000];

    const state = {
      step: 'intro',
      currentEar: 'left',
      answers: new Array(questions.length).fill(null),
      sliderValuesByEar: {
        left: frequencies.map(() => 9),
        right: frequencies.map(() => 9)
      },
      audioContext: null,
      activeOscillator: null,
      activeGain: null,
      activePanner: null,
      activeTimeout: null
    };

    renderIntro();

    function renderIntro() {
      state.step = 'intro';

      const bullets = Array.isArray(config.heroBullets) ? config.heroBullets.filter(Boolean) : [];

      const bulletsHtml = bullets.length
        ? `
          <ul class="hearing-test-list">
            ${bullets.map((item) => `
              <li>
                <span class="hearing-test-list-bullet">-</span>
                <span>${escapeHtml(item)}</span>
              </li>
            `).join('')}
          </ul>
        `
        : '';

      const imageHtml = config.heroImage
        ? `
          <div class="hearing-test-hero-image-wrap">
            <img
              class="hearing-test-hero-image"
              src="${escapeAttribute(config.heroImage)}"
              alt="${escapeAttribute(config.heroTitle || 'Imagem introdutória')}"
              loading="lazy"
            >
          </div>
        `
        : `
          <div class="hearing-test-hero-image-wrap">
            <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;padding:24px;text-align:center;color:#5F6D69;">
              Adicione uma imagem no editor da secção
            </div>
          </div>
        `;

      stage.innerHTML = `
        <div class="hearing-test-screen hearing-test-screen--hero">
          <div class="hearing-test-hero-content">
            <h2 class="hearing-test-title">${escapeHtml(config.heroTitle || 'Descubra como anda a sua audição')}</h2>
            <p class="hearing-test-subtitle">${escapeHtml(config.heroSubtitle || 'O teste demora cerca de 5 minutos. Para obter os melhores resultados:')}</p>
            ${bulletsHtml}
            <div class="hearing-test-btn-row">
              <button
                class="hearing-test-btn"
                type="button"
                data-action="start-questions"
              >
                ${escapeHtml(config.introButtonLabel || 'Iniciar teste')}
              </button>

              ${(Array.isArray(config.extraButtons) ? config.extraButtons : [])
                .filter((button) => button && button.label && button.url)
                .map((button) => `
                  <a
                    class="hearing-test-btn ${button.style === 'secondary' ? 'hearing-test-btn--secondary' : ''}"
                    href="${escapeAttribute(button.url)}"
                  >
                    ${escapeHtml(button.label)}
                  </a>
                `).join('')}
            </div>
          </div>
          <div class="hearing-test-hero-media">
            ${imageHtml}
          </div>
        </div>
      `;

      bindCommonButtons();
    }

    function renderQuestions() {
      state.step = 'questions';
      stopCurrentTone();

      const questionItems = questions.map((question, index) => {
        const value = state.answers[index];

        return `
          <div class="hearing-test-question-item" data-question-index="${index}">
            <div class="hearing-test-question-text">${escapeHtml(question)}</div>
            <div class="hearing-test-answer-group">
              <button
                type="button"
                class="hearing-test-answer-btn ${value === true ? 'is-active' : ''}"
                data-value="yes"
                data-index="${index}"
              >
                Sim
              </button>
              <button
                type="button"
                class="hearing-test-answer-btn ${value === false ? 'is-active' : ''}"
                data-value="no"
                data-index="${index}"
              >
                Não
              </button>
            </div>
          </div>
        `;
      }).join('');

      const allAnswered = state.answers.every((answer) => answer !== null);

      stage.innerHTML = `
        <div class="hearing-test-screen">
          <div class="hearing-test-body hearing-test-body--narrow">
            <h2 class="hearing-test-question-title">${escapeHtml(config.questionTitle || 'Antes do teste, umas perguntas rápidas')}</h2>

            <div class="hearing-test-question-list">
              ${questionItems}
            </div>

            <div class="hearing-test-btn-row hearing-test-btn-row--questions">
              <button
                class="hearing-test-link-btn"
                type="button"
                data-action="continue-sliders"
                ${allAnswered ? '' : 'disabled'}
              >
                <span>Continuar o teste</span>
                <span class="hearing-test-link-btn-icon">→</span>
              </button>
            </div>
          </div>
        </div>
      `;

      stage.querySelectorAll('.hearing-test-answer-btn').forEach((button) => {
        button.addEventListener('click', () => {
          const index = Number(button.getAttribute('data-index'));
          const value = button.getAttribute('data-value') === 'yes';
          state.answers[index] = value;
          renderQuestions();
        });
      });

      bindCommonButtons();
    }

    function renderSliders(ear = 'left') {
      state.step = 'sliders';
      state.currentEar = ear;
      stopCurrentTone();

      const isLeft = ear === 'left';
      const earLabel = isLeft ? 'ouvido esquerdo' : 'ouvido direito';
      const earTitle = isLeft ? 'Ouvido esquerdo' : 'Ouvido direito';
      const nextButtonText = isLeft ? 'Continuar para o ouvido direito' : 'Ver resultado';

      const values = state.sliderValuesByEar[ear];

      const sliderRows = frequencies.map((frequency, index) => {
        const value = values[index] || 9;
        const progress = ((value - 1) / 16) * 100;

        return `
          <div class="hearing-test-slider-row" data-index="${index}">
            <div class="hearing-test-slider-frequency">${escapeHtml(frequency)} Hz</div>

            <button class="hearing-test-slider-btn" type="button" data-action="slider-minus" data-index="${index}" aria-label="Diminuir volume">
              −
            </button>

            <input
              class="hearing-test-range"
              type="range"
              min="1"
              max="17"
              step="1"
              value="${value}"
              data-index="${index}"
              data-frequency="${frequency}"
              style="--slider-progress:${progress}%"
              aria-label="Volume para ${frequency} Hz"
            >

            <button class="hearing-test-slider-btn" type="button" data-action="slider-plus" data-index="${index}" aria-label="Aumentar volume">
              +
            </button>

            <div class="hearing-test-slider-value" data-value-index="${index}">
              Nível ${value}
            </div>
          </div>
        `;
      }).join('');

      stage.innerHTML = `
        <div class="hearing-test-screen">
          <div class="hearing-test-sliders-layout">
            <aside class="hearing-test-sliders-side">
              <div class="hearing-test-step-count">
                ${isLeft ? '1 de 2' : '2 de 2'}
              </div>

              <div class="hearing-test-side-copy">
                <h2 class="hearing-test-side-title">${escapeHtml(earTitle)}</h2>
                <p class="hearing-test-side-text">
                  ${escapeHtml(
                    isLeft
                      ? 'Começamos pelo ouvido esquerdo. Use auscultadores e tente ajustar cada frequência ao volume mínimo que ainda consegue ouvir nesse ouvido.'
                      : 'Agora repita o mesmo processo para o ouvido direito. Ajuste cada frequência ao volume mínimo audível neste ouvido.'
                  )}
                </p>
              </div>

              <div class="hearing-test-side-actions">
                <button class="hearing-test-side-nav-btn" type="button" data-action="${isLeft ? 'back-questions' : 'back-left-ear'}">
                  <span class="hearing-test-side-nav-icon">←</span>
                  <span>Voltar</span>
                </button>
              </div>
            </aside>

            <div class="hearing-test-sliders-main">
              <h2 class="hearing-test-sliders-title">Teste do ${escapeHtml(earLabel)}</h2>
              <p class="hearing-test-sliders-copy">
                ${escapeHtml(config.sliderText || 'Ajuste cada frequência até encontrar o volume mínimo que ainda consegue ouvir.')}
              </p>

              <div class="hearing-test-slider-list">
                ${sliderRows}
              </div>

              <div class="hearing-test-slider-actions">
                <div class="hearing-test-playing-pill" id="hearing-test-playing-pill">
                  A reproduzir som...
                </div>

                <button class="hearing-test-btn" type="button" data-action="${isLeft ? 'finish-left-ear' : 'finish-right-ear'}">
                  ${escapeHtml(nextButtonText)}
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      bindSliderControls();
      bindCommonButtons();
    }

    function bindSliderControls() {
      stage.querySelectorAll('.hearing-test-range').forEach((range) => {
        range.addEventListener('input', () => {
          const index = Number(range.getAttribute('data-index'));
          const value = Number(range.value);
          updateSliderValue(index, value, true);
        });

        range.addEventListener('change', () => {
          const index = Number(range.getAttribute('data-index'));
          const value = Number(range.value);
          updateSliderValue(index, value, true);
        });
      });

      stage.querySelectorAll('[data-action="slider-minus"]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = Number(button.getAttribute('data-index'));
          const currentValues = state.sliderValuesByEar[state.currentEar];
          const nextValue = Math.max(1, (currentValues[index] || 9) - 1);
          updateSliderValue(index, nextValue, true);
        });
      });

      stage.querySelectorAll('[data-action="slider-plus"]').forEach((button) => {
        button.addEventListener('click', () => {
          const index = Number(button.getAttribute('data-index'));
          const currentValues = state.sliderValuesByEar[state.currentEar];
          const nextValue = Math.min(17, (currentValues[index] || 9) + 1);
          updateSliderValue(index, nextValue, true);
        });
      });
    }

    function updateSliderValue(index, value, shouldPlay) {
      const safeValue = Math.max(1, Math.min(17, Number(value) || 9));
      const frequency = frequencies[index];
      const currentValues = state.sliderValuesByEar[state.currentEar];

      currentValues[index] = safeValue;

      const range = stage.querySelector(`.hearing-test-range[data-index="${index}"]`);
      const valueEl = stage.querySelector(`[data-value-index="${index}"]`);

      if (range) {
        const progress = ((safeValue - 1) / 16) * 100;
        range.value = String(safeValue);
        range.style.setProperty('--slider-progress', `${progress}%`);
      }

      if (valueEl) {
        valueEl.textContent = `Nível ${safeValue}`;
      }

      if (shouldPlay && frequency) {
        playTone(frequency, safeValue, state.currentEar);
      }
    }

    function playTone(frequency, sliderValue, ear) {
      stopCurrentTone();

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      if (!state.audioContext) {
        state.audioContext = new AudioContext();
      }

      if (state.audioContext.state === 'suspended') {
        state.audioContext.resume();
      }

      const now = state.audioContext.currentTime;
      const oscillator = state.audioContext.createOscillator();
      const gain = state.audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(Number(frequency), now);

      const volume = sliderValueToGain(sliderValue);

      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), now + 0.04);
      gain.gain.setValueAtTime(Math.max(0.0001, volume), now + 4.8);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 5);

      oscillator.connect(gain);

      if (state.audioContext.createStereoPanner) {
        const panner = state.audioContext.createStereoPanner();
        panner.pan.setValueAtTime(ear === 'left' ? -1 : 1, now);
        gain.connect(panner);
        panner.connect(state.audioContext.destination);
        state.activePanner = panner;
      } else {
        gain.connect(state.audioContext.destination);
      }

      oscillator.start(now);
      oscillator.stop(now + 5.05);

      state.activeOscillator = oscillator;
      state.activeGain = gain;

      showPlayingPill(true, `${frequency} Hz · Nível ${sliderValue}`);

      state.activeTimeout = window.setTimeout(() => {
        showPlayingPill(false);
        state.activeOscillator = null;
        state.activeGain = null;
        state.activePanner = null;
        state.activeTimeout = null;
      }, 5100);
    }

    function stopCurrentTone() {
      if (state.activeTimeout) {
        window.clearTimeout(state.activeTimeout);
        state.activeTimeout = null;
      }

      if (state.activeOscillator) {
        try {
          state.activeOscillator.stop();
        } catch (e) {}
        state.activeOscillator = null;
      }

      if (state.activeGain) {
        try {
          state.activeGain.disconnect();
        } catch (e) {}
        state.activeGain = null;
      }

      if (state.activePanner) {
        try {
          state.activePanner.disconnect();
        } catch (e) {}
        state.activePanner = null;
      }

      showPlayingPill(false);
    }

    function showPlayingPill(isVisible, text) {
      const pill = stage.querySelector('#hearing-test-playing-pill');
      if (!pill) return;

      if (text) {
        pill.textContent = `A reproduzir: ${text}`;
      }

      pill.classList.toggle('is-visible', Boolean(isVisible));
    }

    function sliderValueToGain(value) {
      const normalized = (Math.max(1, Math.min(17, value)) - 1) / 16;
      const minGain = 0.001;
      const maxGain = 0.45;

      return minGain * Math.pow(maxGain / minGain, normalized);
    }

    function renderResultForm() {
      stopCurrentTone();

      stage.innerHTML = `
        <div class="hearing-test-screen">
          <div class="hearing-test-center">
            <div class="hearing-test-center-inner">
              <div style="
                width:min(100%,720px);
                margin:0 auto;
                padding:48px;
                border:1px solid var(--hearing-border);
                border-radius:34px;
                background:rgba(255,255,255,0.65);
                text-align:center;
              ">
                <p style="
                  margin:0 0 14px;
                  color:var(--hearing-muted);
                  font-size:13px;
                  font-weight:700;
                  text-transform:uppercase;
                  letter-spacing:0.04em;
                ">
                  Resultado do teste
                </p>

                <h2 class="hearing-test-center-title">
                  Receba o resultado por email
                </h2>

                <p class="hearing-test-center-copy">
                  Indique-nos os seus dados para lhe enviarmos o resultado do seu teste auditivo por email.
                </p>

                <form id="hearing-test-result-form" style="margin-top:28px;display:grid;gap:16px;text-align:left;">
                  <input class="hearing-test-result-input" name="first_name" type="text" placeholder="Nome" required>

                  <input class="hearing-test-result-input" name="last_name" type="text" placeholder="Apelido" required>

                  <input class="hearing-test-result-input" name="email" type="email" placeholder="Email" required>

                  <label class="hearing-test-result-consent">
                    <input
                      type="checkbox"
                      name="consent"
                      value="1"
                      required
                    >

                    <span>
                      Li e aceito os
                      <a href="/pages/termos-e-condicoes" target="_blank" rel="noopener">
                        Termos e Condições
                      </a>
                      e a
                      <a href="/pages/politica-de-privacidade" target="_blank" rel="noopener">
                        Política de Privacidade
                      </a>.
                    </span>
                  </label>

                  <button class="hearing-test-btn" type="submit">
                    Continuar
                  </button>

                  <p id="hearing-test-result-message" class="hearing-test-disclaimer"></p>
                </form>

                <div class="hearing-test-btn-row" style="justify-content:center;margin-top:18px;">
                  <button class="hearing-test-btn hearing-test-btn--secondary" type="button" data-action="back-right-ear">
                    Voltar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      injectResultFormStyles();

      const resultForm = stage.querySelector('#hearing-test-result-form');
      if (resultForm) {
        resultForm.addEventListener('submit', handleResultFormSubmit);
      }

      bindCommonButtons();
    }

    function injectResultFormStyles() {
      if (document.querySelector('#hearing-test-result-form-styles')) return;

      const style = document.createElement('style');
      style.id = 'hearing-test-result-form-styles';
      style.textContent = `
        #hearing-test-section .hearing-test-result-input {
          width: 100%;
          min-height: 54px;
          padding: 14px 18px;
          border-radius: 999px;
          border: 1px solid var(--hearing-border);
          background: #fff;
          color: var(--hearing-text);
          font-size: 15px;
          outline: none;
        }

        #hearing-test-section .hearing-test-result-input:focus {
          border-color: var(--hearing-primary);
        }

        #hearing-test-section .hearing-test-result-consent {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
          font-size: 13px;
          line-height: 1.5;
          color: var(--hearing-text);
        }

        #hearing-test-section .hearing-test-result-consent input[type="checkbox"] {
          width: 17px;
          height: 17px;
          min-width: 17px;
          margin: 2px 0 0;
          cursor: pointer;
          accent-color: var(--hearing-primary);
        }

        #hearing-test-section .hearing-test-result-consent span {
          flex: 1;
        }

        #hearing-test-section .hearing-test-result-consent a {
          color: inherit;
          text-decoration: underline;
        }

        #hearing-test-section .hearing-test-result-consent a:hover {
          opacity: 0.8;
        }
      `;

      document.head.appendChild(style);
    }

    async function handleResultFormSubmit(event) {
      event.preventDefault();

      const resultForm = event.currentTarget;
      const message = stage.querySelector('#hearing-test-result-message');
      const button = resultForm.querySelector('button[type="submit"]');

      const firstName = resultForm.querySelector('[name="first_name"]').value.trim();
      const lastName = resultForm.querySelector('[name="last_name"]').value.trim();
      const email = resultForm.querySelector('[name="email"]').value.trim();
      const consent = resultForm.querySelector('[name="consent"]');

      if (!firstName || !lastName || !isValidEmail(email)) {
        if (message) {
          message.textContent = 'Por favor, preencha nome, apelido e email válido.';
        }
        return;
      }

      if (!consent?.checked) {
        if (message) {
          message.textContent =
            'Para continuar, deve aceitar os Termos e Condições e a Política de Privacidade.';
        }

        consent?.focus();

        return;
      }

      const leftResult = calculateHearingResult('left');
      const rightResult = calculateHearingResult('right');

      if (button) {
        button.disabled = true;
        button.textContent = 'A preparar resultado...';
      }

      if (message) {
        message.textContent = '';
      }

      try {
        const chartImage = await generateAudiogramImage(leftResult, rightResult);

        if (button) {
          button.textContent = 'A enviar...';
        }

        const response = await fetch(EMAIL_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            email,
            left_result: leftResult,
            right_result: rightResult,
            frequencies,
            chart_image: chartImage
          })
        });

        const json = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(json?.error || 'Não foi possível enviar o email.');
        }

        renderEmailSent(email);
      } catch (error) {
        if (message) {
          message.textContent = error?.message || 'Erro ao enviar o resultado.';
        }

        if (button) {
          button.disabled = false;
          button.textContent = 'Continuar';
        }
      }
    }

    function renderEmailSent(email) {
      stage.innerHTML = `
        <div class="hearing-test-screen">
          <div class="hearing-test-center">
            <div class="hearing-test-center-inner">
              <div style="
                width:min(100%,720px);
                margin:0 auto;
                padding:48px;
                border:1px solid var(--hearing-border);
                border-radius:34px;
                background:rgba(255,255,255,0.65);
                text-align:center;
              ">
                <h2 class="hearing-test-center-title">
                  Resultado enviado
                </h2>

                <p class="hearing-test-center-copy">
                  Enviámos o resultado do seu teste auditivo para <strong>${escapeHtml(email)}</strong>.
                </p>

                <p class="hearing-test-disclaimer">
                  Caso não encontre o email, verifique também a pasta de spam ou promoções.
                </p>

                <div class="hearing-test-btn-row" style="justify-content:center;">
                  <button class="hearing-test-btn hearing-test-btn--secondary" type="button" data-action="restart-test">
                    Recomeçar teste
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;

      bindCommonButtons();
    }

    function generateAudiogramImage(leftResult, rightResult) {
      return new Promise((resolve, reject) => {
        if (typeof Chart === 'undefined') {
          reject(new Error('Chart.js não está carregado.'));
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = 900;
        canvas.height = 520;

        const leftData = frequencies.map((freq) => leftResult.frequencyResults[freq]?.estimatedDb ?? null);
        const rightData = frequencies.map((freq) => rightResult.frequencyResults[freq]?.estimatedDb ?? null);

        const backgroundPlugin = {
          id: 'audiogramBackground',
          beforeDraw(chart) {
            const { ctx, chartArea, scales } = chart;
            if (!chartArea || !scales.y) return;

            const zones = [
              { from: -10, to: 20, color: '#f5f5f5' },
              { from: 20, to: 40, color: '#e3e4e5' },
              { from: 40, to: 60, color: '#d0d2d4' },
              { from: 60, to: 90, color: '#b9bbbe' }
            ];

            zones.forEach((zone) => {
              const y1 = scales.y.getPixelForValue(zone.from);
              const y2 = scales.y.getPixelForValue(zone.to);

              ctx.save();
              ctx.fillStyle = zone.color;
              ctx.fillRect(chartArea.left, y1, chartArea.right - chartArea.left, y2 - y1);
              ctx.restore();
            });
          }
        };

        const chart = new Chart(canvas, {
          type: 'line',
          data: {
            labels: frequencies.map(String),
            datasets: [
              {
                label: 'Ouvido esquerdo',
                data: leftData,
                borderColor: '#1f5aa6',
                backgroundColor: '#1f5aa6',
                borderWidth: 3,
                tension: 0.35,
                pointRadius: 6,
                pointHoverRadius: 6
              },
              {
                label: 'Ouvido direito',
                data: rightData,
                borderColor: '#ff8a2a',
                backgroundColor: '#ff8a2a',
                borderWidth: 3,
                tension: 0.35,
                pointRadius: 6,
                pointHoverRadius: 6
              }
            ]
          },
          options: {
            responsive: false,
            animation: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              title: {
                display: true,
                text: 'Resultado do Teste Auditivo'
              }
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Frequência (Hz)'
                },
                grid: {
                  color: 'rgba(0,0,0,0.08)'
                }
              },
              y: {
                reverse: true,
                min: -10,
                max: 90,
                ticks: {
                  stepSize: 10
                },
                title: {
                  display: true,
                  text: 'Nível auditivo estimado (dB)'
                },
                grid: {
                  color: 'rgba(0,0,0,0.12)'
                }
              }
            }
          },
          plugins: [backgroundPlugin]
        });

        setTimeout(() => {
          const image = canvas.toDataURL('image/png');
          chart.destroy();
          resolve(image);
        }, 100);
      });
    }

    function calculateHearingResult(ear) {
      const values = state.sliderValuesByEar[ear] || [];
      const frequencyResults = {};

      frequencies.forEach((frequency, index) => {
        const sliderValue = values[index] || 1;
        frequencyResults[frequency] = {
          level: sliderValue,
          estimatedDb: sliderValueToEstimatedDb(sliderValue)
        };
      });

      const ptaFrequencies = [500, 1000, 2000, 4000];
      const ptaValues = ptaFrequencies
        .map((freq) => frequencyResults[freq]?.estimatedDb)
        .filter((value) => Number.isFinite(value));

      const valuesToUse = ptaValues.length
        ? ptaValues
        : Object.values(frequencyResults).map((item) => item.estimatedDb);

      const ptaDb = Math.round(
        valuesToUse.reduce((sum, value) => sum + value, 0) / valuesToUse.length
      );

      return {
        ear,
        ptaDb,
        level: classifyHearingLevel(ptaDb),
        frequencyResults
      };
    }

    function sliderValueToEstimatedDb(value) {
      const safeValue = Math.max(1, Math.min(17, Number(value) || 1));
      return Math.round((safeValue - 1) * 5.625);
    }

    function classifyHearingLevel(db) {
      if (db <= 20) return 'Audição Normal';
      if (db <= 40) return 'Perda Ligeira';
      if (db <= 70) return 'Perda Moderada';
      if (db <= 90) return 'Perda Severa';
      return 'Perda Profunda';
    }

    function bindCommonButtons() {
      const startQuestions = stage.querySelector('[data-action="start-questions"]');
      const continueSliders = stage.querySelector('[data-action="continue-sliders"]');
      const backQuestions = stage.querySelector('[data-action="back-questions"]');
      const backLeftEar = stage.querySelector('[data-action="back-left-ear"]');
      const finishLeftEar = stage.querySelector('[data-action="finish-left-ear"]');
      const finishRightEar = stage.querySelector('[data-action="finish-right-ear"]');
      const backRightEar = stage.querySelector('[data-action="back-right-ear"]');
      const restartTest = stage.querySelector('[data-action="restart-test"]');

      if (startQuestions) startQuestions.addEventListener('click', renderQuestions);
      if (continueSliders) continueSliders.addEventListener('click', () => renderSliders('left'));
      if (backQuestions) backQuestions.addEventListener('click', renderQuestions);
      if (backLeftEar) backLeftEar.addEventListener('click', () => renderSliders('left'));
      if (finishLeftEar) finishLeftEar.addEventListener('click', () => renderSliders('right'));
      if (finishRightEar) finishRightEar.addEventListener('click', renderResultForm);
      if (backRightEar) backRightEar.addEventListener('click', () => renderSliders('right'));

      if (restartTest) {
        restartTest.addEventListener('click', () => {
          stopCurrentTone();
          state.currentEar = 'left';
          state.answers = new Array(questions.length).fill(null);
          state.sliderValuesByEar = {
            left: frequencies.map(() => 9),
            right: frequencies.map(() => 9)
          };
          renderIntro();
        });
      }
    }

    window.addEventListener('pagehide', stopCurrentTone);

    function isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    }

    function escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function escapeAttribute(value) {
      return escapeHtml(value);
    }
  }
})();